/**
 * applyPatch — apply one patch document to one base value (30.03.02 §4, §5.1).
 *
 *   applyPatch(base, patch, { layerId, basePath }) → { value, diagnostics, writes }
 *
 * A patch is shaped like the thing it patches. Objects follow RFC 7386: `null`
 * deletes a key, a plain object recurses, anything else replaces wholesale.
 * `{ "$value": x }` writes `x` literally, which is the one way to set a key to
 * `null` or to put an object where a scalar was. Arrays get keyed operations instead, through `{ "$ops": [...] }`, because
 * replacing `columns` wholesale throws away exactly the upgrade safety the
 * format exists for:
 *
 *   set          merge a patch into the matched element (nulls delete, nested $ops apply)
 *   replace      swap the matched element for a literal value
 *   insertBefore / insertAfter   next to the matched element
 *   prepend / append             at either end — the only inserts an unkeyed array allows
 *   remove       drop the matched element
 *   reorder      move the named elements to the front; everything else follows in order
 *
 * Operations apply in list order, each against the result of the one before,
 * so an `insertAfter` followed by a `set` on the inserted element works.
 *
 * **Pure, and proportional to the patch.** Neither input is mutated. The result
 * shares every subtree the patch does not touch with `base` by reference, and
 * the walk follows the patch, never the base — so three keys patched into the
 * 560 KB of cardViewData cost three keys, not 560 KB. An empty patch returns
 * `base` itself: that is the uncustomized-org path and allocates nothing
 * (30.03.00 invariant 1).
 *
 * **Never throws on bad data.** Every problem is a diagnostic and the offending
 * operation is skipped, so the compiler can show a customer the whole list and
 * a preview can still show what would happen. It throws `TypeError` only on
 * programmer error: a patch that is not an object, or a missing `layerId`.
 *
 * `writes` lists the canonical path of every subtree the patch replaced,
 * deleted, inserted, removed or reordered. `resolveLayers` turns it into
 * provenance and conflict detection. An array reorder writes `<path>[$order]`,
 * so two layers reordering the same array overlap, and a layer reordering
 * columns does not overlap one that edits a column's width.
 */

import { arrayKeyFor, declaredKeyFor, isUsableKey } from './arrayKeys.js'
import { diagnostic } from './diagnostics.js'
import {
    elementSegment, identityOf, isValidWhere, joinKey, keyValueOf, matchWhere, whereSegment
} from './keys.js'
import { containsReserved, deepEqual, hasOwn, isOpsDoc, isPlainObject, isValueDoc } from './util.js'

const OPS = new Set(['set', 'replace', 'insertBefore', 'insertAfter', 'prepend', 'append', 'remove', 'reorder'])
const WHERE_OPS = new Set(['set', 'replace', 'insertBefore', 'insertAfter', 'remove'])

export function applyPatch(base, patch, options = {}) {
    if (!isPlainObject(patch)) {
        throw new TypeError('applyPatch: patch must be a plain object')
    }
    const { layerId, basePath = '' } = options
    if (typeof layerId !== 'string' || layerId === '') {
        throw new TypeError('applyPatch: options.layerId is required')
    }
    if (Object.keys(patch).length === 0) {
        return { value: base, diagnostics: [], writes: [] }
    }

    const ctx = { layerId, diagnostics: [], writes: [] }
    const value = applyValue(base, patch, basePath, basePath, ctx)
    return { value, diagnostics: ctx.diagnostics, writes: ctx.writes }
}

// ---------------------------------------------------------------------------
// Context helpers
// ---------------------------------------------------------------------------

function report(ctx, code, path, message, detail) {
    ctx.diagnostics.push(diagnostic(code, { path, layerId: ctx.layerId, message, detail }))
}

function wrote(ctx, path, op) {
    ctx.writes.push({ path, op })
}

const describe = (v) => (v === null ? 'null' : Array.isArray(v) ? 'an array' : `a ${typeof v}`)

// ---------------------------------------------------------------------------
// Values and objects
// ---------------------------------------------------------------------------

/**
 * Apply patch value `pv` to base value `bv`. `path` is the canonical path,
 * `shape` the same path with `[]` for every array step — the form array-key
 * declarations are matched against.
 */
function applyValue(bv, pv, path, shape, ctx) {
    if (isOpsDoc(pv)) {
        if (Object.keys(pv).length !== 1) {
            report(ctx, 'reserved-key', path,
                '`$ops` must be the only key of its object; it is reserved for array operations.')
            return bv
        }
        return applyOps(bv, pv.$ops, path, shape, ctx)
    }

    // `{ $value: x }` writes x literally — the one way to set null, or to put
    // an object where a scalar was.
    let literal = pv
    if (isValueDoc(pv)) {
        if (Object.keys(pv).length !== 1) {
            report(ctx, 'reserved-key', path,
                '`$value` must be the only key of its object; it is reserved for literal values.')
            return bv
        }
        literal = pv.$value
    } else if (isPlainObject(pv)) {
        if (bv === undefined || bv === null) return createObject(pv, path, shape, ctx)
        return mergeObject(bv, pv, path, shape, ctx)
    }

    // A literal: scalar, array, or a $value, replacing whatever is there.
    if (containsReserved(literal)) {
        report(ctx, 'reserved-key', path,
            'A literal value contains `$ops` or `$value`, which are reserved for patch instructions.')
        return bv
    }
    if (Array.isArray(literal) && Array.isArray(bv) && bv.length > 0 && arrayKeyFor(bv, shape) !== null) {
        report(ctx, 'array-wholesale-replace', path,
            'A keyed array is replaced wholesale. Elements a later release adds here will be lost; use $ops to patch it by key.')
    }
    wrote(ctx, path, 'value')
    return literal
}

/**
 * A patch object into an absent key creates the whole subtree, and that is one
 * write at its root — the same as an inserted array element. Recording a write
 * per leaf would make adding a schema property `{ type, sanitize }` look like
 * an edit of `type`, which the mutability policy rightly forbids on existing
 * properties. Diagnostics from inside still surface.
 */
function createObject(pv, path, shape, ctx) {
    const inner = { layerId: ctx.layerId, diagnostics: ctx.diagnostics, writes: [] }
    const value = mergeObject(undefined, pv, path, shape, inner)
    wrote(ctx, path, 'create')
    return value
}

/** RFC 7386 merge of a plain-object patch, copying `bv` only once something changes. */
function mergeObject(bv, pv, path, shape, ctx) {
    let src
    if (bv === undefined || bv === null) src = null
    else if (isPlainObject(bv)) src = bv
    else {
        report(ctx, 'type-mismatch', path,
            `The patch is an object but the base value is ${describe(bv)}.`)
        return bv
    }

    // A patch into an absent key creates it, parents included (§8 case 1).
    let out = src ? null : {}

    for (const key of Object.keys(pv)) {
        const childPath = joinKey(path, key)
        const v = pv[key]
        const target = out ?? src

        if (v === null) {
            // Deleting a key that is not there is a no-op, not a diagnostic (§8 case 2).
            if (target && hasOwn(target, key)) {
                if (!out) out = { ...src }
                delete out[key]
                wrote(ctx, childPath, 'delete')
            }
            continue
        }

        const present = target !== null && hasOwn(target, key)
        const prev = present ? target[key] : undefined
        const next = applyValue(prev, v, childPath, joinKey(shape, key), ctx)
        if (next === undefined) continue
        if (next !== prev || !present) {
            if (!out) out = { ...src }
            out[key] = next
        }
    }
    return out ?? src
}

// ---------------------------------------------------------------------------
// Arrays
// ---------------------------------------------------------------------------

const keyFieldsOf = (keyField) => (Array.isArray(keyField) ? keyField : [keyField])

const isKeyFieldSpec = (kf) =>
    (typeof kf === 'string' && kf !== '') ||
    (Array.isArray(kf) && kf.length > 0 && kf.every(f => typeof f === 'string' && f !== ''))

function applyOps(bv, ops, path, shape, ctx) {
    if (!Array.isArray(ops)) {
        report(ctx, 'invalid-patch', path, '`$ops` must be an array of operations.')
        return bv
    }
    if (bv !== undefined && bv !== null && !Array.isArray(bv)) {
        report(ctx, 'type-mismatch', path,
            `Array operations target ${describe(bv)}; they need an array.`)
        return bv
    }

    // An absent array is an empty one, so `append` works on a list the base does
    // not define (§8 case 3). Copy-on-write: `list` is only copied once changed.
    let list = Array.isArray(bv) ? bv : []
    let changed = false
    const edit = () => {
        if (!changed) { list = list.slice(); changed = true }
        return list
    }
    const elementShape = `${shape}[]`

    ops.forEach((op, index) => {
        if (!isPlainObject(op) || typeof op.op !== 'string') {
            report(ctx, 'invalid-patch', path, `Operation ${index} is not of the form { op: … }.`, { index })
            return
        }
        const detail = { op: op.op, index }
        if (op.where !== undefined) detail.where = op.where
        if (!OPS.has(op.op)) {
            report(ctx, 'unknown-op', path, `Unknown operation "${op.op}".`, detail)
            return
        }

        // Which field keys this array — resolved against the array as it stands
        // now, since earlier operations may have changed it.
        let keyField
        if (op.keyField !== undefined) {
            if (!isKeyFieldSpec(op.keyField)) {
                report(ctx, 'invalid-patch', path, '`keyField` must be a field name or a list of them.', detail)
                return
            }
            if (list.length > 0 && !isUsableKey(list, op.keyField)) {
                report(ctx, 'invalid-patch', path,
                    `keyField ${JSON.stringify(op.keyField)} does not name every element of this array uniquely.`, detail)
                return
            }
            keyField = op.keyField
        } else {
            keyField = list.length > 0 ? arrayKeyFor(list, shape) : (declaredKeyFor(shape) ?? null)
        }

        // --- locate the target of a `where` operation ---------------------
        let target = -1
        if (WHERE_OPS.has(op.op)) {
            if (!isValidWhere(op.where)) {
                report(ctx, 'invalid-patch', path,
                    '`where` must be a non-empty string or an object of scalar values.', detail)
                return
            }
            const wherePath = path + whereSegment(op.where, keyField)
            if (list.length === 0) {
                report(ctx, 'unmatched-where', wherePath,
                    'The array is empty, so nothing matched; operation skipped.', detail)
                return
            }
            if (keyField === null) {
                report(ctx, 'unkeyed-array-op', path,
                    `"${op.op}" needs a keyed array, and this array has no key. Only append, prepend and wholesale replacement are possible.`, detail)
                return
            }
            const { index: hit, count } = matchWhere(list, op.where, keyField)
            if (count === 0) {
                report(ctx, 'unmatched-where', wherePath, 'No element matched; operation skipped.', detail)
                return
            }
            if (count > 1) {
                report(ctx, 'ambiguous-where', wherePath,
                    `${count} elements matched; a where must match exactly one. Operation skipped.`, detail)
                return
            }
            target = hit
        }

        switch (op.op) {
            case 'set': {
                if (!isPlainObject(op.value) || isOpsDoc(op.value) || isValueDoc(op.value)) {
                    report(ctx, 'invalid-patch', path,
                        '`set` takes a patch object for the element; use `replace` for a literal.', detail)
                    return
                }
                const el = list[target]
                if (!isPlainObject(el)) {
                    report(ctx, 'type-mismatch', path, `The matched element is ${describe(el)}, not an object.`, detail)
                    return
                }
                for (const f of keyFieldsOf(keyField)) {
                    if (hasOwn(op.value, f) && !deepEqual(op.value[f], el[f])) {
                        report(ctx, 'invalid-patch', path,
                            `\`set\` may not change the key field "${f}"; use \`replace\` to swap the element.`, detail)
                        return
                    }
                }
                const next = mergeObject(el, op.value, path + elementSegment(el, keyField, target), elementShape, ctx)
                if (next !== el) edit()[target] = next
                return
            }

            case 'replace': {
                if (!checkLiteral(op, list, keyField, target, path, detail, ctx)) return
                const before = path + elementSegment(list[target], keyField, target)
                wrote(ctx, before, 'replace')
                const after = path + elementSegment(op.value, keyField, target)
                if (after !== before) wrote(ctx, after, 'replace')
                edit()[target] = op.value
                return
            }

            case 'insertBefore':
            case 'insertAfter':
            case 'prepend':
            case 'append': {
                if (!checkLiteral(op, list, keyField, -1, path, detail, ctx)) return
                const at = op.op === 'insertBefore' ? target
                    : op.op === 'insertAfter' ? target + 1
                    : op.op === 'prepend' ? 0
                    : list.length
                edit().splice(at, 0, op.value)
                wrote(ctx, path + elementSegment(op.value, keyField, at), 'insert')
                return
            }

            case 'remove': {
                wrote(ctx, path + elementSegment(list[target], keyField, target), 'remove')
                edit().splice(target, 1)
                return
            }

            case 'reorder': {
                const ids = op.ids
                if (!Array.isArray(ids) || ids.length === 0 ||
                    !ids.every(id => typeof id === 'string' || typeof id === 'number')) {
                    report(ctx, 'invalid-patch', path, '`reorder` takes a non-empty `ids` list of key values.', detail)
                    return
                }
                if (new Set(ids.map(String)).size !== ids.length) {
                    report(ctx, 'invalid-patch', path, '`reorder` names the same key twice.', detail)
                    return
                }
                if (list.length === 0) {
                    for (const id of ids) {
                        report(ctx, 'unmatched-where', path + whereSegment(String(id), keyField),
                            'The array is empty, so nothing matched; id ignored.', detail)
                    }
                    return
                }
                if (keyField === null) {
                    report(ctx, 'unkeyed-array-op', path,
                        '"reorder" needs a keyed array, and this array has no key.', detail)
                    return
                }

                // key value → indices, built once: O(n), not O(n × ids).
                const byKey = new Map()
                list.forEach((el, j) => {
                    const v = keyValueOf(el, keyField)
                    if (v === undefined || Array.isArray(v)) return
                    const k = String(v)
                    if (!byKey.has(k)) byKey.set(k, [])
                    byKey.get(k).push(j)
                })

                const front = []
                for (const id of ids) {
                    const hits = byKey.get(String(id)) ?? []
                    const idPath = path + whereSegment(String(id), keyField)
                    if (hits.length === 0) {
                        report(ctx, 'unmatched-where', idPath, 'No element has this key; id ignored.', detail)
                    } else if (hits.length > 1) {
                        report(ctx, 'ambiguous-where', idPath, `${hits.length} elements have this key; id ignored.`, detail)
                    } else {
                        front.push(hits[0])
                    }
                }

                // Named elements first, in the order given; every other element
                // keeps its relative order behind them. A column a later release
                // adds is not named, so it is never lost (§4.5).
                const named = new Set(front)
                const next = front.map(j => list[j]).concat(list.filter((_, j) => !named.has(j)))
                wrote(ctx, `${path}[$order]`, 'reorder')
                if (next.some((el, j) => el !== list[j])) {
                    list = next
                    changed = true
                }
                return
            }
        }
    })

    return changed ? list : bv
}

/**
 * Validate a literal element about to enter a keyed array: no `$ops`, carries
 * the key, and does not collide with an element already there. Duplicate keys
 * would break every `where` downstream (§8 cases 7 and 8). `exclude` is the
 * index being replaced, which may keep its own key.
 */
function checkLiteral(op, list, keyField, exclude, path, detail, ctx) {
    if (op.value === undefined) {
        report(ctx, 'invalid-patch', path, `"${op.op}" needs a \`value\`.`, detail)
        return false
    }
    if (containsReserved(op.value)) {
        report(ctx, 'reserved-key', path, 'The value contains `$ops` or `$value`, which are reserved for patch instructions.', detail)
        return false
    }
    if (keyField === null) return true

    const id = identityOf(op.value, keyField)
    if (id === undefined) {
        report(ctx, 'invalid-patch', path,
            `The value has no ${JSON.stringify(keyField)}, and every element of this array must carry its key.`, detail)
        return false
    }
    for (let j = 0; j < list.length; j++) {
        if (j !== exclude && identityOf(list[j], keyField) === id) {
            report(ctx, 'invalid-patch', path, `An element with key ${id} already exists.`, detail)
            return false
        }
    }
    return true
}
